// src/app/not-found.tsx
"use client";

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Binary,
  Bug,
  CheckCircle,
  Cpu,
  Home,
  Lock,
  RotateCcw,
  Shield,
  Terminal,
  WifiOff,
  Zap,
} from "lucide-react";

/**
 * Small deterministic helpers (SSR/hydration-safe).
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function hashString(input: string): number {
  // FNV-1a 32-bit
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function createPRNG(seed: number) {
  // Mulberry32
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let x = Math.imul(t ^ (t >>> 15), 1 | t);
    x ^= x + Math.imul(x ^ (x >>> 7), 61 | x);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

function formatHex(n: number, width = 8) {
  const s = (n >>> 0).toString(16).toUpperCase();
  return s.padStart(width, "0");
}

type FxProps = {
  seed: number;
  tick: number;
  enabled: boolean;
  isDarkMode: boolean;
};

const codeSnippets = [
  "root@rekontech:~# systemctl restart network",
  "const encryptData = (payload) => {",
  "async function bypassSecurity() {",
  "ERROR: Connection timeout",
  "SELECT * FROM inventory WHERE tenant_id = ?;",
  "sudo chmod +x /usr/local/bin/patch",
  "git checkout -b hotfix/not-found",
  "npm run build -- --profile",
  "ACCESS DENIED: invalid token",
  "Initializing quantum firewall...",
  "Decrypting payload... 0xA7F3C1",
  "POST /api/auth/refresh 200 (12ms)",
  "WARN: rate limit exceeded",
  "Compiling modules... 98%",
  "export const runtime = 'edge';",
];

const HackerCodeBackground: React.FC<Pick<FxProps, "seed" | "isDarkMode" | "enabled">> = ({
  seed,
  isDarkMode,
  enabled,
}) => {
  const id = useId();

  const columns = useMemo(() => {
    const prng = createPRNG(seed ^ hashString(id));
    const count = 7; // slightly denser than before
    const perColumnLines = 18;

    return Array.from({ length: count }).map((_, col) => {
      const speed = clamp(16 + prng() * 22, 16, 40); // seconds
      const delay = -prng() * 12;
      const opacity = clamp(0.18 + prng() * 0.22, 0.12, 0.42);
      const blur = clamp(prng() * 0.8, 0, 0.8);
      const skew = (prng() - 0.5) * 8;
      const x = (col / (count - 1)) * 100;
      const hue = Math.floor(prng() * 40) - 20;

      const lines = Array.from({ length: perColumnLines }).map(() => {
        const pick = codeSnippets[Math.floor(prng() * codeSnippets.length)];
        const noiseA = formatHex(Math.floor(prng() * 0xffffffff), 6);
        const noiseB = formatHex(Math.floor(prng() * 0xffffffff), 4);
        return `${noiseA}  ${pick}  #${noiseB}`;
      });

      return { col, speed, delay, opacity, blur, skew, x, hue, lines };
    });
  }, [seed, id]);

  const baseText = isDarkMode ? "text-cyan-200/40" : "text-slate-700/40";
  const glow = isDarkMode ? "drop-shadow-[0_0_10px_rgba(34,211,238,0.25)]" : "";

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Soft radial glow */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(800px circle at 50% 35%, rgba(34,211,238,0.18), transparent 55%), radial-gradient(700px circle at 70% 70%, rgba(99,102,241,0.14), transparent 55%)",
          opacity: enabled ? 1 : 0.6,
        }}
      />

      {/* Columns */}
      <div className="absolute inset-0">
        {columns.map((c) => (
          <motion.div
            key={c.col}
            className={`absolute top-0 ${baseText} ${glow} font-mono text-[10px] leading-4`}
            style={{
              left: `${c.x}%`,
              width: "14%",
              opacity: c.opacity,
              filter: `blur(${c.blur}px)`,
              transform: `translateX(-50%) skewX(${c.skew}deg)`,
              mixBlendMode: isDarkMode ? "screen" : "multiply",
            }}
            animate={{ y: ["-35%", "135%"] }}
            transition={{
              duration: c.speed,
              repeat: Infinity,
              ease: "linear",
              delay: c.delay,
            }}
          >
            <div className="space-y-2">
              {c.lines.map((line, i) => (
                <div key={i} className="relative">
                  {/* subtle highlight strip */}
                  <span
                    className="absolute -inset-x-2 -inset-y-1 opacity-0"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(34,211,238,0.14), transparent)",
                    }}
                  />
                  <span
                    className="inline-block"
                    style={{
                      transform: `translateZ(0)`,
                      filter: `hue-rotate(${c.hue}deg)`,
                    }}
                  >
                    {line}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Scanlines */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "repeating-linear-gradient(180deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 3px)",
          mixBlendMode: isDarkMode ? "overlay" : "multiply",
        }}
      />

      {/* Moving scan beam */}
      <motion.div
        className="absolute -inset-x-10 h-40 opacity-30"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(34,211,238,0.22), rgba(99,102,241,0.18), transparent)",
          filter: "blur(8px)",
          mixBlendMode: isDarkMode ? "screen" : "multiply",
        }}
        animate={{ y: ["-20%", "120%"] }}
        transition={{ duration: enabled ? 4.5 : 7, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
};

type GlitchElementProps = {
  children: React.ReactNode;
  intensity?: number; // 1..3
  enabled: boolean;
  seed: number;
  tick: number;
  className?: string;
};

const GlitchElement: React.FC<GlitchElementProps> = ({
  children,
  intensity = 1,
  enabled,
  seed,
  tick,
  className,
}) => {
  const id = useId();
  const [glitched, setGlitched] = useState(false);

  const prng = useMemo(() => createPRNG(seed ^ hashString(id)), [seed, id]);

  useEffect(() => {
    if (!enabled) {
      setGlitched(false);
      return;
    }

    // Decide if this tick should glitch (deterministic per element + seed)
    const chance = clamp(0.08 * intensity, 0.08, 0.28);
    const roll = prng();
    if (roll < chance) {
      setGlitched(true);
      const t = window.setTimeout(() => setGlitched(false), 120 + Math.floor(prng() * 120));
      return () => window.clearTimeout(t);
    } else {
      setGlitched(false);
    }
    // tick intentionally referenced to run every global pulse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, enabled, intensity]);

  return (
    <div className={`relative ${className ?? ""}`}>
      {/* Base */}
      <div className={glitched ? "opacity-0" : "opacity-100"}>{children}</div>

      {/* RGB split */}
      {glitched && (
        <>
          <div
            className="absolute inset-0 opacity-70"
            style={{ transform: `translate(${intensity}px, -${intensity}px)` }}
          >
            <div className="text-red-400">{children}</div>
          </div>
          <div
            className="absolute inset-0 opacity-70"
            style={{ transform: `translate(-${intensity}px, ${intensity}px)` }}
          >
            <div className="text-cyan-400">{children}</div>
          </div>

          {/* Horizontal jitter slices */}
          <div className="absolute inset-0 opacity-50">
            <div
              className="absolute left-0 right-0 top-[18%] h-[10%]"
              style={{
                transform: `translateX(${(prng() - 0.5) * 18}px)`,
                background: "rgba(255,255,255,0.06)",
              }}
            />
            <div
              className="absolute left-0 right-0 top-[56%] h-[8%]"
              style={{
                transform: `translateX(${(prng() - 0.5) * 22}px)`,
                background: "rgba(34,211,238,0.07)",
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

const GlitchDataLines: React.FC<Pick<FxProps, "seed" | "tick" | "enabled" | "isDarkMode">> = ({
  seed,
  tick,
  enabled,
  isDarkMode,
}) => {
  const id = useId();
  const prng = useMemo(() => createPRNG(seed ^ hashString(id)), [seed, id]);

  const lines = useMemo(() => {
    const count = 22;
    return Array.from({ length: count }).map((_, i) => {
      const x1 = prng() * 100;
      const x2 = prng() * 100;
      const y = (i / (count - 1)) * 100;
      const width = clamp(0.5 + prng() * 1.8, 0.5, 2.3);
      const speed = clamp(3 + prng() * 5, 3, 8);
      return { i, x1, x2, y, width, speed };
    });
  }, [prng]);

  const glitchFlag = enabled && tick % 3 === 0;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 100 100" preserveAspectRatio="none">
        {lines.map((l) => (
          <motion.line
            key={l.i}
            x1={l.x1}
            y1={l.y}
            x2={l.x2}
            y2={l.y}
            stroke={
              glitchFlag
                ? "rgba(255,255,255,0.55)"
                : isDarkMode
                ? "rgba(34,211,238,0.25)"
                : "rgba(0,0,0,0.12)"
            }
            strokeWidth={l.width}
            strokeDasharray={glitchFlag ? "1 1" : "2 6"}
            initial={{ opacity: 0.25 }}
            animate={{ opacity: [0.15, 0.45, 0.15] }}
            transition={{ duration: l.speed, repeat: Infinity, ease: "easeInOut" }}
          />
        ))}
      </svg>
    </div>
  );
};

const SystemStatusPanel: React.FC<Pick<FxProps, "seed" | "tick" | "enabled" | "isDarkMode">> = ({
  seed,
  tick,
  enabled,
  isDarkMode,
}) => {
  const id = useId();
  const prng = useMemo(() => createPRNG(seed ^ hashString(id)), [seed, id]);

  const isGlitch = enabled && tick % 2 === 0 && prng() < 0.35;

  const uptime = useMemo(() => {
    // stable per seed
    const base = Math.floor((seed % 10_000) / 10);
    const hours = 42 + (base % 70);
    const minutes = 10 + (base % 50);
    return `${hours}h ${minutes}m`;
  }, [seed]);

  const packets = useMemo(() => {
    const n = (seed ^ 0x9e3779b9) >>> 0;
    return (Math.floor(n % 9000) + 1000).toLocaleString();
  }, [seed]);

  return (
    <div className="absolute top-6 left-6 pointer-events-none hidden sm:block">
      <div
        className={`w-60 p-4 rounded-xl border ${
          isDarkMode ? "bg-black/40 border-cyan-500/20" : "bg-white/60 border-slate-300/40"
        } backdrop-blur-md shadow-[0_0_20px_rgba(34,211,238,0.10)]`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Cpu size={14} className={isDarkMode ? "text-cyan-400" : "text-slate-700"} />
            <span className={`text-xs font-mono ${isDarkMode ? "text-cyan-200" : "text-slate-800"}`}>SYSTEM STATUS</span>
          </div>
          <div
            className={`px-2 py-0.5 rounded text-[10px] font-mono ${
              isGlitch ? "bg-red-500/20 text-red-300" : "bg-green-500/20 text-green-300"
            }`}
          >
            {isGlitch ? "CORRUPTED" : "ACTIVE"}
          </div>
        </div>

        <div className="space-y-2 text-[11px] font-mono">
          {[
            { label: "UPTIME", value: uptime },
            { label: "PACKETS", value: packets },
            { label: "FIREWALL", value: isGlitch ? "DEGRADED" : "ONLINE" },
            { label: "ENCRYPT", value: "AES-256" },
          ].map((row) => (
            <div key={row.label} className="flex items-center justify-between">
              <span className={isDarkMode ? "text-cyan-200/70" : "text-slate-600"}>{row.label}</span>
              <span className={isDarkMode ? "text-white/90" : "text-slate-900"}>{row.value}</span>
            </div>
          ))}
        </div>

        <div className="mt-3 h-1 rounded bg-white/5 overflow-hidden">
          <motion.div
            className="h-full"
            style={{
              background: "linear-gradient(90deg, rgba(34,211,238,0.55), rgba(99,102,241,0.55), rgba(34,211,238,0.55))",
            }}
            animate={{ x: ["-40%", "120%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </div>
    </div>
  );
};

const HackingChecklist: React.FC<Pick<FxProps, "tick" | "enabled" | "isDarkMode">> = ({ tick, enabled, isDarkMode }) => {
  const steps = [
    { id: 1, text: "Locate route", icon: <Terminal size={12} /> },
    { id: 2, text: "Validate auth token", icon: <Shield size={12} /> },
    { id: 3, text: "Patch navigation stack", icon: <Zap size={12} /> },
    { id: 4, text: "Rebuild caches", icon: <RotateCcw size={12} /> },
    { id: 5, text: "Restore access", icon: <CheckCircle size={12} /> },
  ];

  const activeStep = enabled ? (Math.floor(tick / 2) % steps.length) : 0;

  return (
    <div className="absolute bottom-6 right-6 w-[min(18rem,92vw)] pointer-events-none hidden md:block">
      <div
        className={`p-4 rounded-xl border ${
          isDarkMode ? "bg-black/35 border-cyan-500/20" : "bg-white/60 border-slate-300/40"
        } backdrop-blur-md shadow-[0_0_20px_rgba(34,211,238,0.08)]`}
      >
        <div className="flex items-center gap-2 mb-3">
          <Binary size={14} className={isDarkMode ? "text-cyan-400" : "text-slate-800"} />
          <span className={`text-xs font-mono ${isDarkMode ? "text-cyan-200" : "text-slate-800"}`}>
            RECOVERY SEQUENCE
          </span>
        </div>

        <div className="space-y-2">
          {steps.map((step, index) => {
            const status =
              index < activeStep ? "completed" : index === activeStep ? "active" : "pending";

            return (
              <motion.div
                key={step.id}
                className={`flex items-center gap-2 p-2 rounded text-xs font-mono ${
                  status === "completed"
                    ? "bg-green-500/10"
                    : status === "active"
                    ? "bg-cyan-500/10"
                    : "bg-white/5"
                }`}
                initial={{ opacity: 0, x: -16 }}
                animate={{
                  opacity: status === "pending" ? 0.35 : 1,
                  x: 0,
                }}
                transition={{ delay: index * 0.06 }}
              >
                <span
                  className={`${
                    status === "completed"
                      ? "text-green-400"
                      : status === "active"
                      ? "text-cyan-400"
                      : isDarkMode
                      ? "text-white/50"
                      : "text-slate-600"
                  }`}
                >
                  {status === "completed" ? <CheckCircle size={12} /> : step.icon}
                </span>
                <span className={isDarkMode ? "text-white/80" : "text-slate-800"}>{step.text}</span>
                {status === "active" && (
                  <motion.span
                    className="ml-auto text-cyan-400"
                    animate={{ opacity: [0.2, 1, 0.2] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  >
                    ▌
                  </motion.span>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function NotFound() {
  const router = useRouter();

  const [mounted, setMounted] = useState(false);
  const [seed, setSeed] = useState(1337); // stable SSR seed (updates after mount)
  const [glitchMode, setGlitchMode] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [fxTick, setFxTick] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useTransform(mouseY, [-20, 20], [6, -6]);
  const rotateY = useTransform(mouseX, [-20, 20], [-6, 6]);

  const springRotateX = useSpring(rotateX, { stiffness: 160, damping: 22 });
  const springRotateY = useSpring(rotateY, { stiffness: 160, damping: 22 });

  useEffect(() => {
    setMounted(true);

    // Generate a stronger seed after mount (avoids SSR/client mismatch).
    try {
      const buf = new Uint32Array(2);
      crypto.getRandomValues(buf);
      setSeed((buf[0] ^ (buf[1] << 1)) >>> 0);
    } catch {
      setSeed((Date.now() ^ 0x9e3779b9) >>> 0);
    }
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const resolveIsDark = () => {
      const root = document.documentElement;
      const classDark = root.classList.contains("dark");
      const classLight = root.classList.contains("light");
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

      // If app manages theme via classes, trust that first.
      if (classDark) return true;
      if (classLight) return false;

      // Otherwise fall back to system preference.
      return prefersDark;
    };

    setIsDarkMode(resolveIsDark());

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const onMediaChange = () => setIsDarkMode(resolveIsDark());
    mediaQuery.addEventListener("change", onMediaChange);

    const observer = new MutationObserver(() => setIsDarkMode(resolveIsDark()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => {
      mediaQuery.removeEventListener("change", onMediaChange);
      observer.disconnect();
    };
  }, [mounted]);

  // Single global FX pulse (instead of many intervals)
  useEffect(() => {
    if (!mounted) return;
    let alive = true;

    const enableFx = glitchMode;
    const ms = enableFx ? 650 : 1200;

    const interval = window.setInterval(() => {
      if (!alive) return;
      if (document.visibilityState === "hidden") return;
      setFxTick((t) => (t + 1) % 10_000);
    }, ms);

    return () => {
      alive = false;
      window.clearInterval(interval);
    };
  }, [mounted, glitchMode]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    mouseX.set(clamp(x / 18, -20, 20));
    mouseY.set(clamp(y / 18, -20, 20));
  };

  const sessionId = useMemo(() => `0x${formatHex(seed ^ 0x1234abcd, 10)}`, [seed]);

  const enableFx = mounted && glitchMode;

  const palette = useMemo(() => {
    if (isDarkMode) {
      return {
        bgA: "from-slate-950 via-slate-900 to-black",
        text: "text-white",
        subText: "text-slate-200/80",
        accent: "rgba(34,211,238,1)", // cyan-400
        accentSoft: "rgba(34,211,238,0.18)",
        accent2: "rgba(99,102,241,1)", // indigo-500
        border: "rgba(34,211,238,0.22)",
        btnBg: "rgba(2,6,23,0.55)",
        btnBorder: "rgba(34,211,238,0.28)",
      };
    }
    return {
      bgA: "from-slate-50 via-white to-slate-100",
      text: "text-slate-900",
      subText: "text-slate-700/80",
      accent: "rgba(14,165,233,1)", // sky-500
      accentSoft: "rgba(14,165,233,0.16)",
      accent2: "rgba(37,99,235,1)", // blue-600
      border: "rgba(15,23,42,0.16)",
      btnBg: "rgba(255,255,255,0.65)",
      btnBorder: "rgba(15,23,42,0.18)",
    };
  }, [isDarkMode]);

  const prng = useMemo(() => createPRNG(seed ^ 0xa5a5a5a5), [seed]);

  const particles = useMemo(() => {
    if (!mounted || !enableFx) return [];
    const count = 14;
    const items = Array.from({ length: count }).map((_, i) => ({
      id: `${seed}-${i}`,
      left: prng() * 100,
      top: prng() * 100,
      size: 6 + prng() * 10,
      delay: prng() * 0.6,
      dur: 0.8 + prng() * 0.8,
      alpha: 0.12 + prng() * 0.24,
    }));
    return items;
  }, [mounted, enableFx, prng, seed]);

  return (
    <div
      className={`fixed inset-0 z-[9999] overflow-hidden bg-gradient-to-br ${palette.bgA} ${palette.text}`}
      ref={containerRef}
      onMouseMove={handleMouseMove}
    >
      {/* Background (slight blur only on background) */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          filter: isDarkMode ? "blur(0.9px) saturate(1.12)" : "blur(0.7px) saturate(1.06)",
          opacity: isDarkMode ? 0.9 : 0.85,
        }}
      >
        <HackerCodeBackground seed={seed} isDarkMode={isDarkMode} enabled={mounted} />
        <GlitchDataLines seed={seed} tick={fxTick} enabled={enableFx} isDarkMode={isDarkMode} />

        {/* Scanlines */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "100% 8px",
            opacity: isDarkMode ? 0.09 : 0.06,
          }}
        />

        {/* Moving scan beam */}
        <motion.div
          className="absolute -inset-x-10 h-28 pointer-events-none"
          initial={{ y: "-20%" }}
          animate={{ y: ["-20%", "120%"] }}
          transition={{ duration: 6.5, repeat: Infinity, ease: "linear" }}
          style={{
            background: `linear-gradient(90deg, transparent, ${palette.accentSoft}, transparent)`,
            opacity: isDarkMode ? 0.55 : 0.4,
          }}
        />
      </div>

      {/* Foreground clarity layer */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            isDarkMode
              ? "radial-gradient(circle at 50% 40%, rgba(34,211,238,0.14), transparent 55%), radial-gradient(circle at 20% 70%, rgba(99,102,241,0.10), transparent 55%), radial-gradient(circle at 85% 65%, rgba(34,211,238,0.08), transparent 55%)"
              : "radial-gradient(circle at 50% 40%, rgba(14,165,233,0.12), transparent 55%), radial-gradient(circle at 20% 70%, rgba(37,99,235,0.08), transparent 55%), radial-gradient(circle at 85% 65%, rgba(14,165,233,0.06), transparent 55%)",
          opacity: isDarkMode ? 1 : 0.7,
        }}
      />

      {/* Panels */}
      <SystemStatusPanel seed={seed} tick={fxTick} enabled={enableFx} isDarkMode={isDarkMode} />
      <HackingChecklist tick={fxTick} enabled={enableFx} isDarkMode={isDarkMode} />

      {/* Content (no modal/card) */}
      <div className="relative z-10 h-full w-full flex items-center justify-center px-6 py-12">
        <motion.div
          style={{
            rotateX: springRotateX,
            rotateY: springRotateY,
            transformStyle: "preserve-3d",
          }}
          className="w-full max-w-3xl"
        >
          <div className="flex flex-col items-center text-center">
            <div className="flex flex-wrap items-center justify-center gap-3 text-[11px] uppercase tracking-[0.28em]">
              <GlitchElement enabled={enableFx} seed={seed} tick={fxTick} intensity={2}>
                <span
                  className="inline-flex items-center gap-2 rounded-full px-3 py-1"
                  style={{
                    background: isDarkMode ? "rgba(2,6,23,0.45)" : "rgba(255,255,255,0.65)",
                    border: `1px solid ${palette.border}`,
                    boxShadow: `0 0 24px ${palette.accentSoft}`,
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <AlertTriangle className="h-4 w-4" />
                  ERROR 404
                </span>
              </GlitchElement>

              <span
                className="inline-flex items-center gap-2 rounded-full px-3 py-1"
                style={{
                  background: isDarkMode ? "rgba(2,6,23,0.32)" : "rgba(255,255,255,0.55)",
                  border: `1px solid ${palette.border}`,
                  backdropFilter: "blur(10px)",
                }}
              >
                <Binary className="h-4 w-4" />
                SESSION {sessionId}
              </span>
            </div>

            <div className="mt-8">
              <GlitchElement enabled={enableFx} seed={seed} tick={fxTick} intensity={4}>
                <h1
                  className="font-black leading-none tracking-tight"
                  style={{
                    fontSize: "clamp(4.2rem, 12vw, 8.2rem)",
                    textShadow: isDarkMode
                      ? "0 0 28px rgba(34,211,238,0.25), 0 0 64px rgba(99,102,241,0.14)"
                      : "0 0 18px rgba(14,165,233,0.18), 0 0 44px rgba(37,99,235,0.12)",
                  }}
                >
                  404
                </h1>
              </GlitchElement>

              <GlitchElement enabled={enableFx} seed={seed} tick={fxTick} intensity={2}>
                <p className={`mt-2 text-sm sm:text-base ${palette.subText}`}>
                  The route you requested is missing or access is restricted.
                </p>
              </GlitchElement>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 w-full">
              <Link
                href="/"
                className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold transition"
                style={{
                  background: palette.btnBg,
                  border: `1px solid ${palette.btnBorder}`,
                  boxShadow: `0 0 40px ${palette.accentSoft}`,
                  backdropFilter: "blur(12px)",
                }}
              >
                <Home className="h-4 w-4" />
                Go home
                <ArrowRight className="h-4 w-4 opacity-80" />
              </Link>

              <button
                type="button"
                onClick={() => router.back()}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold transition"
                style={{
                  background: palette.btnBg,
                  border: `1px solid ${palette.btnBorder}`,
                  backdropFilter: "blur(12px)",
                }}
              >
                <RotateCcw className="h-4 w-4" />
                Go back
              </button>

              <button
                type="button"
                onClick={() => router.refresh()}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold transition"
                style={{
                  background: palette.btnBg,
                  border: `1px solid ${palette.btnBorder}`,
                  backdropFilter: "blur(12px)",
                }}
              >
                <Cpu className="h-4 w-4" />
                Retry
              </button>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-xs">
              <button
                type="button"
                onClick={() => setGlitchMode((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 transition"
                style={{
                  background: isDarkMode ? "rgba(2,6,23,0.45)" : "rgba(255,255,255,0.65)",
                  border: `1px solid ${palette.border}`,
                  backdropFilter: "blur(12px)",
                }}
              >
                <Bug className="h-4 w-4" />
                GLITCH: {glitchMode ? "ON" : "OFF"}
              </button>

              <span className={palette.subText}>
                Tip: if you still see headers/sidebars, this screen overlays them to keep the 404 clean.
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Glitch particles (deterministic, render only after mount) */}
      <AnimatePresence>
        {enableFx &&
          particles.map((p) => (
            <motion.div
              key={p.id}
              className="absolute rounded-full pointer-events-none"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: p.alpha, scale: [0.6, 1.2, 0.8], y: [0, -14, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: p.dur, repeat: Infinity, delay: p.delay }}
              style={{
                left: `${p.left}%`,
                top: `${p.top}%`,
                width: p.size,
                height: p.size,
                background: `radial-gradient(circle, ${palette.accent} 0%, transparent 70%)`,
                filter: "blur(0.5px)",
              }}
            />
          ))}
      </AnimatePresence>
    </div>
  );
}
